import apiClient from '@/api/apiClient';
import { handleError, getAuthData, prepareModel } from '../../utils/storeHelpers';

export default {
  namespaced: true,
  state: {
    schema: {},
    modelSlides: [],
    backDevChildhoodId: '',
    isNewRecord: true,
    status: '',
  },

  getters: {
    schema: (state) => state.schema,
    modelSlides: (state) => state.modelSlides,
    backDevChildhoodId: (state) => state.backDevChildhoodId,
    isNewRecord: (state) => state.isNewRecord,
    status: (state) => state.status,
  },

  mutations: {
    SET_SCHEMA(state, schema) {
      state.schema = schema;
    },
    SET_RECORDS(state, { recordsData }) {
      state.modelSlides = Array.isArray(recordsData?.modelSlides) ? recordsData.modelSlides : [];
      state.backDevChildhoodId = recordsData?.backDevChildhoodId ? recordsData.backDevChildhoodId : '';
      state.isNewRecord = Boolean(recordsData?.isNewRecord);
    },
    SET_STATUS(state, status) {
      state.status = status;
    },
  },

  actions: {
    async fetchSchema({ commit }) {
      try {
        commit('SET_STATUS', 'loading');
        const responses = await Promise.all([
          apiClient.anxADxChildhoodSchema(),
          apiClient.catAttachment(),
          apiClient.catAgeInterval(),
          apiClient.catBond(),
          apiClient.catFrequency(),
          apiClient.catCurrentSexGender(),
        ]);
        const [
          schemaRes,
          catAttachment,
          catAgeInterval,
          catBond,
          catFrequency,
          catCurrentSexGender,
        ] = responses.map((res) => res?.data ?? {});

        if (!schemaRes.schemaFormItems) throw new Error('Estructura de schema inválida.');

        let schema = structuredClone(schemaRes);
        schema.schemaFormItems[0].schemaForm.group2.schema.i1_child_attachment_id.items =
          catAttachment.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i2_child_attachment_id.items =
          catAttachment.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i3_child_attachment_id.items =
          catAttachment.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i1_child_attachment_age_beginning.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i1_child_attachment_age_final.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i2_child_attachment_age_beginning.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i2_child_attachment_age_final.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i3_child_attachment_age_beginning.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i3_child_attachment_age_final.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i1_child_attachment_carer1_bond_id.items =
          catBond.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i1_child_attachment_carer2_bond_id.items =
          catBond.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i1_child_attachment_carer3_bond_id.items =
          catBond.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i2_child_attachment_carer1_bond_id.items =
          catBond.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i2_child_attachment_carer2_bond_id.items =
          catBond.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i2_child_attachment_carer3_bond_id.items =
          catBond.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i3_child_attachment_carer1_bond_id.items =
          catBond.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i3_child_attachment_carer2_bond_id.items =
          catBond.data ?? [];
        schema.schemaFormItems[0].schemaForm.group2.schema.i3_child_attachment_carer3_bond_id.items =
          catBond.data ?? [];
        schema.schemaFormItems[1].schemaForm.group2.schema.child_imaginary_friends_age_beginning.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[1].schemaForm.group2.schema.child_imaginary_friends_age_final.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[1].schemaForm.group2.schema.child_tantrums_age_beginning.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[1].schemaForm.group2.schema.child_tantrums_age_final.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[1].schemaForm.group2.schema.child_tantrums_frequency_id.items =
          catFrequency.data ?? [];
        schema.schemaFormItems[1].schemaForm.group3.schema.i5_child_sex_age_beginning.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[1].schemaForm.group3.schema.i5_child_sex_age_final.items =
          catAgeInterval.data ?? [];
        schema.schemaFormItems[1].schemaForm.group3.schema.i5_child_sex_frequency_id.items =
          catFrequency.data ?? [];
        schema.schemaFormItems[1].schemaForm.group3.schema.i5_child_sex_gender_id.items =
          catCurrentSexGender.data ?? [];

        let modelFormItems = [
          { modelForm: { group1: {}, group2: {} } },
          { modelForm: { group1: {}, group2: {}, group3: {} } },
        ];

        let modelSlides = modelFormItems.map((item, i) => ({
          modelForm: item.modelForm,
          schemaForm: schema.schemaFormItems[i].schemaForm,
        }));

        const isNewRecord = true;
        const recordsData = { modelSlides, isNewRecord };
        commit('SET_SCHEMA', schema);
        commit('SET_RECORDS', recordsData);
      } catch (error) {
        handleError(error, commit);
      }
    },

    async fetchRecords({ commit, rootState, getters, dispatch }) {
      try {
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        const res = await apiClient.modelANxADxChildhood(clinicalHistories_id, institutions_profes_id);
        let recordsData = res?.data?.data ?? {};
        const dataModelFormItems = recordsData.modelSlides;

        if (Object.keys(getters.schema).length === 0) {
          await dispatch('fetchSchema');
        }
        const schema = JSON.parse(JSON.stringify(getters.schema));

        let modelSlides = dataModelFormItems.map((item, i) => ({
          modelForm: item.modelForm,
          schemaForm: schema.schemaFormItems[i].schemaForm,
        }));

        if (recordsData.isNewRecord === true) {
          modelSlides.back_dev_childhood_id = dataModelFormItems[0].modelForm.back_dev_childhood_id;
        }

        recordsData.modelSlides = modelSlides;
        commit('SET_RECORDS', { recordsData });
        commit('SET_STATUS', 'loaded');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async createRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'create');
        const preparedModel = prepareModel(rootState, model);
        await apiClient.createANxADxChildhood(preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async updateRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'update');
        const preparedModel = prepareModel(rootState, model);
        const id = model.backDevChildhoodId;
        await apiClient.updateANxADxChildhood(id, preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },
  },
};
